<template>
  <div class="materials-history">
    <!-- Desktop and tablet view -->
    <div v-if="!isMobile" class="desktop-view">
      <div v-if="loading" class="flex justify-center items-center py-4">
        <v-progress-circular indeterminate color="primary" size="small"></v-progress-circular>
      </div>
      
      <div v-else-if="displayMaterials.length > 0" class="materials-container">
        <p class="text-center text-gray-400 text-sm mb-3">
          {{ isShowingDemoMaterials ? t('materials.tryDemo') : t('materials.history') }}
        </p>
        
        <div class="flex items-center justify-center gap-3">
          <div 
            v-for="(material, index) in visibleMaterials" 
            :key="material.material_id || `demo-${index}`"
            class="material-item"
            @click="selectMaterial(material)"
          >
            <div class="relative group cursor-pointer">
              <div class="relative w-[120px] h-[90px] rounded-lg overflow-hidden bg-neutral-800">
                <img
                  v-if="material.cover_url || !isVideoFormat(material)"
                  :src="material.cover_url || material.convert_url || material.url"
                  class="w-full h-full object-cover"
                  :alt="material.material_id || `demo-${index}`"
                >
                <video
                  v-else
                  :src="`${material.convert_url || material.url}#t=0.1`"
                  class="w-full h-full object-cover"
                  muted
                  playsinline
                  preload="metadata"
                />
                
                <!-- Video duration indicator -->
                <div v-if="isVideoFormat(material) && material.duration" class="absolute bottom-1 left-1">
                  <div class="bg-black bg-opacity-60 px-1 py-0.5 rounded text-xs text-white">
                    {{ formatDuration(material.duration) }}
                  </div>
                </div>
                
                <!-- Delete button for user materials -->
                <div 
                  v-if="isLoggedIn && !material.isDemo"
                  @click.stop="confirmDelete(material)"
                  class="w-7 h-7 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 duration-200 hover:bg-red-600 cursor-pointer absolute top-1 right-1"
                >
                  <v-icon size="16" color="white">mdi-delete-outline</v-icon>
                </div>
              </div>
            </div>
          </div>
          
          <!-- More button -->
          <div 
            v-if="hasMore"
            class="material-item"
            @click="openMaterialSelector"
          >
            <div class="w-[120px] h-[90px] rounded-lg bg-neutral-800 flex items-center justify-center cursor-pointer hover:bg-neutral-700 transition-colors">
              <div class="flex items-center gap-1">
                <span class="text-gray-400 text-sm">{{ t('materials.more') }}</span>
                <v-icon size="small" color="gray">mdi-chevron-right</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Mobile view -->
    <div v-else class="mobile-view">
      <div v-if="loading" class="flex justify-center items-center py-4">
        <v-progress-circular indeterminate color="primary" size="small"></v-progress-circular>
      </div>
      
      <div v-else-if="displayMaterials.length > 0" class="materials-container">
        <p class="text-center text-gray-400 text-sm mb-3">
          {{ isShowingDemoMaterials ? t('materials.tryDemo') : t('materials.history') }}
        </p>
        
        <div class="flex flex-col items-center gap-3">
          <!-- Materials grid -->
          <div class="flex items-center justify-center gap-2">
            <div 
              v-for="(material, index) in mobileVisibleMaterials" 
              :key="material.material_id || `demo-${index}`"
              class="material-item-mobile"
              @click="selectMaterial(material)"
            >
              <div class="relative group">
                <div class="relative w-[92px] h-[69px] rounded-lg overflow-hidden bg-neutral-800">
                  <img
                    v-if="material.cover_url || !isVideoFormat(material)"
                    :src="material.cover_url || material.convert_url || material.url"
                    class="w-full h-full object-cover"
                    :alt="material.material_id || `demo-${index}`"
                  >
                  <video
                    v-else
                    :src="`${material.convert_url || material.url}#t=0.1`"
                    class="w-full h-full object-cover"
                    muted
                    playsinline
                    preload="metadata"
                  />
                  
                  <!-- Video duration indicator -->
                  <div v-if="isVideoFormat(material) && material.duration" class="absolute bottom-1 left-1">
                    <div class="bg-black bg-opacity-60 px-1 py-0.5 rounded text-xs text-white">
                      {{ formatDuration(material.duration) }}
                    </div>
                  </div>
                  
                  <!-- Delete button for user materials -->
                  <div 
                    v-if="isLoggedIn && !material.isDemo"
                    @click.stop="confirmDelete(material)"
                    class="w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center absolute top-1 right-1"
                  >
                    <v-icon size="14" color="white">mdi-delete-outline</v-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- More button below materials -->
          <div 
            v-if="displayMaterials.length > 3"
            @click="openMaterialSelector"
            class="text-gray-400 text-sm flex items-center justify-center gap-1 py-2 cursor-pointer"
          >
            <span>{{ t('materials.more') }}</span>
            <v-icon size="small" color="gray">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Material Selector Dialog -->
    <MaterialSelector 
      v-model="materialSelectorVisible"
      @select="onMaterialSelected"
    />
    
    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card class="bg-neutral-800 !rounded-2xl">
        <v-card-title class="text-center pt-6">
          <span class="text-xl font-bold text-white">Confirm Deletion</span>
        </v-card-title>
        <v-card-text class="text-center pb-2">
          <p class="text-gray-300">Are you sure you want to delete the history of uploaded materials?</p>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 flex gap-3">
          <v-btn 
            color="primary" 
            variant="flat"
            size="large"
            @click="deleteDialog = false"
            class="flex-1 !bg-orange-600 !text-black font-semibold !rounded-full"
          >
            Cancel
          </v-btn>
          <v-btn 
            variant="flat"
            size="large"
            @click="deleteMaterial"
            class="flex-1 !bg-orange-900/40 !text-orange-600 font-semibold !rounded-full"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import api from '~/api';
import appConfig from '~/app.config';
import type { Material, MaterialWithStatus } from '~/types';
import MaterialSelector from './MaterialSelector.vue';

const emit = defineEmits<{
  'select': [material: MaterialWithStatus];
}>();

const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();
const display = useDisplay();
const isMobile = computed(() => display.xs.value);
const auth = useAuthStore();
const snackbar = useSnackbarStore();
const globalLoading = useLoadingStore();

// 使用 useState 来与 /my/uploads 页面保持一致
const matObj = useState<MaterialWithStatus>('matObj', () => null);

// 使用 useState 来共享已加载的材料数据，避免重复加载
const sharedMaterials = useState<MaterialWithStatus[]>('sharedMaterials', () => []);
const sharedMaterialsTotal = useState<number>('sharedMaterialsTotal', () => 0);
const sharedMaterialsLoaded = useState<boolean>('sharedMaterialsLoaded', () => false);

const loading = ref(false);
const materials = ref<MaterialWithStatus[]>([]);
const materialSelectorVisible = ref(false);
const deleteDialog = ref(false);
const materialToDelete = ref<MaterialWithStatus | null>(null);

const isLoggedIn = computed(() => auth.isLogin);

// Demo materials (shown when user is not logged in or has no uploads)
const demoMaterials = [
  {
    material_id: 'demo-1',
    url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_1.mp4',
    convert_url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_1.mp4',
    cover_url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_1_thumbnail.webp',
    format: 'mp4',
    detect_status: 1,
    matStat: 'success' as const,
    isDemo: true,
    duration: 10
  },
  {
    material_id: 'demo-2',
    url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_2.webp',
    convert_url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_2.webp',
    cover_url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_2_thumbnail.webp',
    format: 'webp',
    detect_status: 1,
    matStat: 'success' as const,
    isDemo: true
  },
  {
    material_id: 'demo-3',
    url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_3.webp',
    convert_url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_3.webp',
    cover_url: 'https://storage.googleapis.com/aivideo_faceswap_storage/demo_material/demo_3_thumbnail.webp',
    format: 'webp',
    detect_status: 1,
    matStat: 'success' as const,
    isDemo: true
  }
] as Partial<MaterialWithStatus>[];

const displayMaterials = computed(() => {
  // Show user materials if logged in and has materials
  if (isLoggedIn.value && materials.value.length > 0) {
    // Filter out failed materials (but keep them in the materials array for pagination)
    return materials.value.filter(mat => mat.matStat !== 'failed' && mat.matStat !== 'prohibit');
  }
  // Otherwise show demo materials
  return demoMaterials as MaterialWithStatus[];
});

// Check if we're showing demo materials
const isShowingDemoMaterials = computed(() => {
  return !isLoggedIn.value || materials.value.length === 0;
});

const visibleMaterials = computed(() => {
  // Desktop: show up to 5 materials
  return displayMaterials.value.slice(0, 5);
});

const mobileVisibleMaterials = computed(() => {
  // Mobile: show up to 3 materials
  return displayMaterials.value.slice(0, 3);
});

const hasMore = computed(() => {
  // Show more button if:
  // 1. User is logged in and has more materials in total
  // 2. Never show for demo materials
  if (!isLoggedIn.value) return false;
  // Check against the total count from shared state or API response
  return sharedMaterialsTotal.value > materials.value.length || displayMaterials.value.length > (isMobile.value ? 3 : 5);
});

const isVideoFormat = (mat: Partial<MaterialWithStatus>): boolean => {
  // First check if format field exists and use it
  if (mat.format) {
    return appConfig.videoExt.some(format => format.ext === mat.format);
  }
  
  // Fallback to URL extension checking
  if (!mat.url && !mat.convert_url && !mat.cover_url) return false;
  const urlExt = (mat.cover_url || mat.convert_url || mat.url).split('.').pop()?.toLowerCase();
  return appConfig.videoExt.some(format => format.ext === urlExt);
};

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const handleMats = (mats: Material[]) => {
  return mats.map(mat => {
    let matStat: 'analyzing' | 'success' | 'failed' | 'prohibit' = 'success';
    switch (mat.detect_status) {
      case 0:
        matStat = 'analyzing';
        break;
      case 1:
        matStat = 'success';
        break;
      case -1:
        matStat = 'failed';
        break;
      case -2:
        matStat = 'prohibit';
        break;
    }
    return { ...mat, matStat };
  });
};

const loadMaterials = async () => {
  // Only load if user is logged in
  if (!isLoggedIn.value) {
    materials.value = [];
    sharedMaterials.value = [];
    sharedMaterialsLoaded.value = false;
    return;
  }
  
  // If we already have shared materials loaded, use them
  if (sharedMaterialsLoaded.value && sharedMaterials.value.length > 0) {
    materials.value = sharedMaterials.value.slice(0, 10);
    return;
  }
  
  try {
    loading.value = true;
    // Only load a small number for quick access (slightly more than displayed to check for "More" button)
    const resp = await api.getMyUploads('all', 1, 10);
    if (resp) {
      // Don't filter - show all materials
      const allMaterials = handleMats(resp.lists || []);
      materials.value = allMaterials;
      
      // Store in shared state
      sharedMaterials.value = allMaterials;
      sharedMaterialsTotal.value = resp.total;
      sharedMaterialsLoaded.value = true;
    }
  } catch (error) {
    console.error('Failed to load materials:', error);
    materials.value = [];
    sharedMaterials.value = [];
    sharedMaterialsLoaded.value = false;
  } finally {
    loading.value = false;
  }
};

const selectMaterial = (material: MaterialWithStatus | Partial<MaterialWithStatus>) => {
  if (!isLoggedIn.value && !material.isDemo) {
    // Redirect to login if not logged in and trying to select non-demo material
    router.push(localePath('/login'));
    return;
  }
  
  if (material.matStat === 'failed' || material.matStat === 'prohibit') {
    snackbar.showError(t('materials.invalid'));
    return;
  }
  if (material.matStat === 'analyzing') {
    snackbar.showError(t('materials.analyzing'));
    return;
  }
  
  // Set the material in useState to maintain consistency with /my/uploads page flow
  matObj.value = material as MaterialWithStatus;
  
  emit('select', material as MaterialWithStatus);
};

const openMaterialSelector = () => {
  if (!isLoggedIn.value) {
    router.push(localePath('/login'));
    return;
  }
  materialSelectorVisible.value = true;
};

const onMaterialSelected = (material: MaterialWithStatus) => {
  emit('select', material);
};

const confirmDelete = (material: MaterialWithStatus | Partial<MaterialWithStatus>) => {
  if (material.isDemo) return;
  materialToDelete.value = material as MaterialWithStatus;
  deleteDialog.value = true;
};

const deleteMaterial = async () => {
  if (!materialToDelete.value) return;
  
  try {
    globalLoading.startLoading(t('materials.deleting'));
    await api.deleteMat(materialToDelete.value.material_id);
    snackbar.showSnackbar({ color: 'success', message: t('materials.deleted') });
    
    // Clear shared state to force reload
    sharedMaterialsLoaded.value = false;
    sharedMaterials.value = [];
    
    // Reload materials
    await loadMaterials();
  } catch (error) {
    snackbar.showError(error.response?.data?.message || t('materials.deleteFailed'));
  } finally {
    globalLoading.stopLoading();
    deleteDialog.value = false;
    materialToDelete.value = null;
  }
};

onMounted(() => {
  // Always try to load materials, even if not logged in (will fail gracefully)
  loadMaterials();
});

// Watch for login status changes
watch(isLoggedIn, (newVal) => {
  if (newVal) {
    loadMaterials();
  }
});
</script>

<style scoped>
.materials-history {
  width: 100%;
}

.materials-container {
  padding: 1rem 0;
}

.material-item {
  transition: transform 0.2s;
}

.material-item:hover {
  transform: scale(1.05);
}

.material-item-mobile {
  transition: opacity 0.2s;
}

.material-item-mobile:active {
  opacity: 0.8;
}
</style>